import { supabase } from '../supabase'; // Ajustar caminho se necessário
import type { Database, Json } from '../../types/supabase'; // <<< Importar Json aqui
import { getUserTotalXP, addUserXP } from './gamificationService'; // Importar addUserXP e getUserTotalXP
import { calculateLevel } from './gamificationUtils'; // Importar calculateLevel
// Importar calculateLevel de onde estiver definido (provavelmente gamificationUtils.ts)
// import { calculateLevel } from './gamificationUtils'; 
import { SupabaseClient, User } from '@supabase/supabase-js';
// import { emitEvent } from '../eventBus'; // Remover - Não usado nas novas funções
import { type XpHistoryDetails } from './types'; // Manter apenas o tipo necessário
import { toast } from 'react-hot-toast';

// Tipos específicos para badges (podem ir para types.ts ou database.ts se preferir)
type BadgeRow = Database['public']['Tables']['badges']['Row'];
type UserBadgeRow = Database['public']['Tables']['user_badges']['Row'];
// Definindo um tipo mais específico para criteria_data, se possível
type CriteriaData = BadgeRow['criteria_data']; 
// Helper type para criteriaData que esperamos ser um objeto
type CriteriaObject = { [key: string]: Json | undefined }; 

// Tipo combinado para badges do perfil do usuário
export type UserProfileBadge = BadgeRow & Pick<UserBadgeRow, 'unlocked_at' | 'is_highlighted'>;

/**
 * Busca os IDs de todos os usuários no sistema.
 * TODO: Considerar paginação para sistemas com muitos usuários.
 */
export async function getAllUserIds(): Promise<string[]> {
  const { data, error } = await supabase
    .from('profiles') // Assumindo que a tabela 'profiles' contém todos os usuários
    .select('id');

  if (error) {
    console.error('[BadgeService:getAllUserIds] Erro ao buscar IDs de usuários:', error);
    return [];
  }
  return data ? data.map(profile => profile.id) : [];
}

/**
 * Busca todas as badges ativas no sistema.
 */
export async function getAllActiveBadges(): Promise<BadgeRow[]> {
  const { data, error } = await supabase
    .from('badges')
    .select('*')
    .eq('is_active', true);

  if (error) {
    console.error('[BadgeService:getAllActiveBadges] Erro ao buscar badges:', error);
    return [];
  }
  return data || [];
}

/**
 * Busca uma badge específica pelo seu ID.
 */
export async function getBadgeById(badgeId: string): Promise<BadgeRow | null> {
   const { data, error } = await supabase
    .from('badges')
    .select('*')
    .eq('id', badgeId)
    .maybeSingle();

  if (error) {
    console.error(`[BadgeService:getBadgeById] Erro ao buscar badge ${badgeId}:`, error);
    return null;
  }
  return data;
}

/**
 * Busca uma badge específica pela sua chave (badge_key).
 */
export async function getBadgeByKey(badgeKey: string): Promise<BadgeRow | null> {
   const { data, error } = await supabase
    .from('badges')
    .select('*')
    .eq('badge_key', badgeKey)
    .maybeSingle();

  if (error) {
    console.error(`[BadgeService:getBadgeByKey] Erro ao buscar badge com chave ${badgeKey}:`, error);
    return null;
  }
  return data;
}


/**
 * Busca todas as badges desbloqueadas por um usuário, incluindo detalhes da badge.
 */
export async function getUserBadgesWithDetails(userId: string): Promise<(UserBadgeRow & { badges: BadgeRow | null })[]> {
  const { data, error } = await supabase
    .from('user_badges')
    .select(`
      *,
      badges (*)
    `)
    .eq('user_id', userId);

  if (error) {
    console.error(`[BadgeService:getUserBadgesWithDetails] Erro ao buscar badges do usuário ${userId}:`, error);
    return [];
  }
  // O tipo inferido pelo Supabase com select aninhado pode ser complexo.
  // Fazemos um cast para o tipo esperado, assumindo que 'badges' será um objeto ou null.
  return (data as (UserBadgeRow & { badges: BadgeRow | null })[]) || [];
}

/**
 * Verifica se um usuário já possui uma badge específica.
 */
export async function userHasBadge(userId: string, badgeId: string): Promise<boolean> {
  const { data, error, count } = await supabase
    .from('user_badges')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('badge_id', badgeId);

  if (error) {
    console.error(`[BadgeService:userHasBadge] Erro ao verificar badge ${badgeId} para usuário ${userId}:`, error);
    return false; // Assumir que não tem em caso de erro
  }
  return (count ?? 0) > 0;
}

/**
 * Concede uma badge a um usuário.
 * Assume que os critérios JÁ foram validados externamente.
 * Lida com a concessão de XP bônus.
 * TODO: Emitir evento BADGE_UNLOCKED.
 */
export async function awardBadgeToUser(userId: string, badgeId: string): Promise<boolean> {
  const badge = await getBadgeById(badgeId);
  if (!badge) {
    console.error(`[BadgeService:awardBadgeToUser] Badge com ID ${badgeId} não encontrada.`);
    return false;
  }

  const alreadyHas = await userHasBadge(userId, badge.id);
  if (alreadyHas) {
    console.log(`[BadgeService:awardBadgeToUser] Usuário ${userId} já possui a badge ${badge.badge_key}.`);
    return false;
  }

  console.log(`[BadgeService:awardBadgeToUser] Tentando conceder badge '${badge.badge_key}' para ${userId}`);

  const { error: insertError } = await supabase
    .from('user_badges')
    .insert({ user_id: userId, badge_id: badgeId });

  if (insertError) {
    console.error(`[BadgeService:awardBadgeToUser] Erro ao inserir badge para ${userId}:`, insertError);
    return false;
  }

  console.log(`[BadgeService:awardBadgeToUser] Badge ${badge.badge_key} concedida com sucesso para ${userId}.`);

  // Conceder XP bônus
  if (badge.xp_reward && badge.xp_reward > 0) {
    console.log(`[BadgeService:awardBadgeToUser] Concedendo ${badge.xp_reward} XP pela badge ${badge.badge_key}.`);
    try {
      // Chamada explícita com nomes de argumento para clareza
      // A definição em gamificationService é: 
      // export async function addUserXP(userId: string, xpToAdd: number, sourceType: string, sourceId?: string, actionId?: string, details?: XpHistoryDetails)
      const xpResult = await addUserXP(
          supabase,                   // <<< Adicionar supabase client
          userId,                     // userId: string
          badge.xp_reward,            // xpToAdd: number
          'BADGE_REWARD',             // sourceType: string
          badge.id                    // sourceId?: string (usando badgeId como sourceId)
          // actionId?: string (não aplicável para recompensa de badge)
          // details?: XpHistoryDetails (não temos detalhes extras aqui)
      );

      if (!xpResult.success) {
        console.error(`[BadgeService:awardBadgeToUser] Falha ao conceder XP bônus (Resultado):`, xpResult.error);
      } else {
        console.log(`[BadgeService:awardBadgeToUser] XP bônus de ${badge.xp_reward} concedido.`);
      }
    } catch (xpError) {
      console.error(`[BadgeService:awardBadgeToUser] Erro ao chamar addUserXP:`, xpError);
    }
  }

  // TODO: Emitir evento/notificação BADGE_UNLOCKED
  console.log(`// TODO: Emitir evento/notificação BADGE_UNLOCKED para ${userId} - Badge: ${badge.name}`);

  return true;
}


// --- Funções de Verificação de Critérios ---

/**
 * Verifica se um usuário atende aos critérios para uma badge específica e a concede se sim.
 * Retorna true se a badge foi concedida nesta chamada, false caso contrário.
 */
export async function checkAndPotentiallyAwardBadge(userId: string, badge: BadgeRow): Promise<boolean> {
  if (!badge.is_active) return false; // Não processar badges inativas

  // Verificar se o usuário já possui a badge
  const alreadyHas = await userHasBadge(userId, badge.id);
  if (alreadyHas) return false;

  // Verificar critérios de data (sazonal)
  const now = new Date();
  if (badge.start_date && now < new Date(badge.start_date)) return false;
  if (badge.end_date && now > new Date(badge.end_date)) return false;

  let meetsCriteria = false;
  const criteriaType = badge.criteria_type;
  const criteriaData = badge.criteria_data;

  try {
    // Usar um switch baseado na CHAVE do critério definida em criteria_data para badges automáticas
    if (criteriaType === 'automatic' && criteriaData && typeof criteriaData === 'object' && 'condition_key' in criteriaData) {
        const conditionKey = criteriaData.condition_key as string;
        
        // Verificando se criteriaData é um objeto antes de acessar suas propriedades
        const isCriteriaObject = (data: Json | null | undefined): data is CriteriaObject => {
            return typeof data === 'object' && data !== null && !Array.isArray(data);
        };

        switch (conditionKey) {
          case 'post_count':
            meetsCriteria = await checkPostCountCriteria(userId, criteriaData);
            break;
          case 'like_given_count':
            meetsCriteria = await checkLikeGivenCountCriteria(userId, criteriaData);
            break;
          case 'like_received_count':
            meetsCriteria = await checkLikeReceivedCountCriteria(userId, criteriaData);
            break;
          case 'comment_count':
            meetsCriteria = await checkCommentCountCriteria(userId, criteriaData);
            break;
          case 'collection_size':
            meetsCriteria = await checkCollectionSizeCriteria(userId, criteriaData);
            break;
          case 'wishlist_size':
            meetsCriteria = await checkWishlistSizeCriteria(userId, criteriaData);
            break;
          case 'rating_count':
            meetsCriteria = await checkRatingCountCriteria(userId, criteriaData);
            break;
          case 'complexity_rating_count':
            meetsCriteria = await checkComplexityRatingCountCriteria(userId, criteriaData);
            break;
          case 'session_count':
            meetsCriteria = await checkSessionCountCriteria(userId, criteriaData);
            break;
          case 'connection_count':
            meetsCriteria = await checkConnectionCountCriteria(userId, criteriaData);
            break;
          case 'xp_threshold':
            meetsCriteria = await checkXPThresholdCriteria(userId, criteriaData);
            break;
          case 'level_reach':
            meetsCriteria = await checkLevelReachCriteria(userId, criteriaData);
            break;
          case 'badges_count':
             meetsCriteria = await checkBadgesCountCriteria(userId, criteriaData);
             break;
          // case 'login_streak': // Comentado pois a tabela user_streaks não existe
          //    meetsCriteria = await checkLoginStreakCriteria(userId, criteriaData);
          //    break;
          case 'market_transactions':
             meetsCriteria = await checkMarketTransactionsCriteria(userId, criteriaData);
             break;
          // Casos de especialista requerem verificação de tipo mais robusta
          case 'category_specialist':
              if (isCriteriaObject(criteriaData) && typeof criteriaData.category === 'string' && typeof criteriaData.count_required === 'number') {
                meetsCriteria = await checkCategorySpecialistCriteria(userId, criteriaData.category, criteriaData.count_required);
              } else {
                console.error('[BadgeService:checkAndPotentiallyAwardBadge] Dados de critério inválidos para category_specialist', criteriaData);
              }
              break;
          case 'theme_specialist':
              if (isCriteriaObject(criteriaData) && typeof criteriaData.theme === 'string' && typeof criteriaData.count_required === 'number') {
                meetsCriteria = await checkThemeSpecialistCriteria(userId, criteriaData.theme, criteriaData.count_required);
              } else {
                console.error('[BadgeService:checkAndPotentiallyAwardBadge] Dados de critério inválidos para theme_specialist', criteriaData);
              }
              break;
          case 'mechanic_specialist':
              if (isCriteriaObject(criteriaData) && typeof criteriaData.mechanic === 'string' && typeof criteriaData.count_required === 'number') {
                meetsCriteria = await checkMechanicSpecialistCriteria(userId, criteriaData.mechanic, criteriaData.count_required);
              } else {
                console.error('[BadgeService:checkAndPotentiallyAwardBadge] Dados de critério inválidos para mechanic_specialist', criteriaData);
              }
              break;
          default:
            console.warn(`[BadgeService:checkAndPotentiallyAwardBadge] Chave de condição não reconhecida: ${conditionKey}`);
            meetsCriteria = false;
        }
    } else if (criteriaType !== 'manual') {
        // Logar se o tipo de critério não for 'automatic' (com condition_key) ou 'manual'
        console.warn(`[BadgeService:checkAndPotentiallyAwardBadge] Tipo de critério '${criteriaType}' ou formato de criteriaData não suportado para badge ${badge.id}`);
    }

    // Conceder a badge se os critérios foram atendidos
    if (meetsCriteria) {
      const awarded = await awardBadgeToUser(userId, badge.id);
      return awarded; // Retorna true se foi concedida AGORA
    }

  } catch (error) {
    console.error(`[BadgeService:checkAndPotentiallyAwardBadge] Erro ao verificar badge ${badge.id} para usuário ${userId}:`, error);
  }

  return false; // Não concedeu a badge nesta chamada
}

/**
 * Verifica todas as badges ativas para um usuário e concede as que ele cumpre os critérios.
 */
export async function checkAndAwardAllPossibleBadges(userId: string): Promise<string[]> {
    const awardedBadgeIds: string[] = [];
    const activeBadges = await getAllActiveBadges();

    console.log(`[BadgeService:checkAndAwardAllPossibleBadges] Verificando ${activeBadges.length} badges ativas para usuário ${userId}`);

    for (const badge of activeBadges) {
        const wasAwarded = await checkAndPotentiallyAwardBadge(userId, badge);
        if (wasAwarded) {
            awardedBadgeIds.push(badge.id);
        }
    }

     if (awardedBadgeIds.length > 0) {
        console.log(`[BadgeService:checkAndAwardAllPossibleBadges] Novas badges concedidas para ${userId}:`, awardedBadgeIds);
    } else {
         console.log(`[BadgeService:checkAndAwardAllPossibleBadges] Nenhuma nova badge concedida para ${userId}.`);
     }

    return awardedBadgeIds;
}


// --- Implementações Específicas de Critérios ---

async function checkXPThresholdCriteria(userId: string, criteria: CriteriaData | null): Promise<boolean> {
  if (!criteria || typeof criteria !== 'object' || !('xp_required' in criteria)) return false;
  const xpRequired = criteria.xp_required as number;
  // Assumindo que getUserTotalXP busca o valor atual de user_xp.total_xp
  const userXPResult = await supabase.from('user_xp').select('total_xp').eq('user_id', userId).single();
   if (userXPResult.error || !userXPResult.data) {
     console.error('[BadgeService:checkXPThresholdCriteria] Erro ao buscar XP do usuário:', userXPResult.error);
     return false;
   }
  return userXPResult.data.total_xp >= xpRequired;
}

async function checkLevelReachCriteria(userId: string, criteria: CriteriaData | null): Promise<boolean> {
    if (!criteria || typeof criteria !== 'object' || !('level_required' in criteria)) return false;
    const levelRequired = criteria.level_required as number;
     // Assumindo que getUserTotalXP busca o valor atual de user_xp.current_level
    const userLevelResult = await supabase.from('user_xp').select('current_level').eq('user_id', userId).single();
    if (userLevelResult.error || !userLevelResult.data) {
        console.error('[BadgeService:checkLevelReachCriteria] Erro ao buscar Nível do usuário:', userLevelResult.error);
        return false;
    }
    return userLevelResult.data.current_level >= levelRequired;

   // Alternativa se current_level não for confiável ou se precisar recalcular:
   // const userXPResult = await supabase.from('user_xp').select('total_xp').eq('user_id', userId).single();
   // if (userXPResult.error || !userXPResult.data) return false;
   // const userLevel = calculateLevel(userXPResult.data.total_xp); // Precisa importar calculateLevel
   // return userLevel >= levelRequired;
}


async function checkPostCountCriteria(userId: string, criteria: CriteriaData | null): Promise<boolean> {
    if (!criteria || typeof criteria !== 'object' || !('count_required' in criteria)) return false;
    const countRequired = criteria.count_required as number;
    const { count, error } = await supabase
        .from('posts')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .is('parent_post_id', null); 
     if (error) {
        console.error('[BadgeService:checkPostCountCriteria] Erro:', error); return false;
    }
    return (count ?? 0) >= countRequired;
}

async function checkLikeGivenCountCriteria(userId: string, criteria: CriteriaData | null): Promise<boolean> {
    if (!criteria || typeof criteria !== 'object' || !('count_required' in criteria)) return false;
    const countRequired = criteria.count_required as number;
    const { count, error } = await supabase
        .from('post_likes')
        .select('post_id', { count: 'exact', head: true })
        .eq('user_id', userId);
     if (error) {
        console.error('[BadgeService:checkLikeGivenCountCriteria] Erro:', error); return false;
    }
    return (count ?? 0) >= countRequired;
}

async function checkLikeReceivedCountCriteria(userId: string, criteria: CriteriaData | null): Promise<boolean> {
    if (!criteria || typeof criteria !== 'object' || !('count_required' in criteria)) return false;
    const countRequired = criteria.count_required as number;

    // Abordagem 3: Query direta com count (simulando, pode precisar ajustar)
    // Contar likes em posts onde o autor do post é o userId
    const { count: directCount, error: directError } = await supabase
      .from('posts')
      .select('post_likes(count)', {count: 'exact'})
      .eq('user_id', userId);

      if(directError){
          console.error('[BadgeService:checkLikeReceivedCountCriteria] Erro ao contar likes recebidos (join implícito):', directError);
          return false;
      }

      // Assumindo que o Supabase retorna um array onde cada item tem uma propriedade `post_likes` que é um array com um objeto `{ count: number }`
      // Precisamos somar esses counts.
      let totalLikesReceived = 0;
      if (Array.isArray(directCount)) { 
         totalLikesReceived = directCount.reduce((sum, post) => {
           // Verifica se post.post_likes existe, é um array, e tem pelo menos um elemento com 'count'
           if (post && Array.isArray(post.post_likes) && post.post_likes.length > 0 && typeof post.post_likes[0].count === 'number') {
             return sum + post.post_likes[0].count;
           }
           return sum;
         }, 0);
      } else if (typeof directCount === 'number') {
         // Caso menos provável, mas seguro verificar
         totalLikesReceived = directCount; 
      }

      console.log(`[BadgeService:checkLikeReceivedCountCriteria] Likes recebidos contados: ${totalLikesReceived}`);
      return totalLikesReceived >= countRequired;
}


async function checkCommentCountCriteria(userId: string, criteria: CriteriaData | null): Promise<boolean> {
    if (!criteria || typeof criteria !== 'object' || !('count_required' in criteria)) return false;
    const countRequired = criteria.count_required as number;
    const { count, error } = await supabase
        .from('posts')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .not('parent_post_id', 'is', null); 
     if (error) {
        console.error('[BadgeService:checkCommentCountCriteria] Erro:', error); return false;
    }
    return (count ?? 0) >= countRequired;
}

async function checkCollectionSizeCriteria(userId: string, criteria: CriteriaData | null): Promise<boolean> {
    if (!criteria || typeof criteria !== 'object' || !('count_required' in criteria)) return false;
    const countRequired = criteria.count_required as number;
    const { count, error } = await supabase
        .from('user_games')
        .select('game_id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('owned', true); 
     if (error) {
        console.error('[BadgeService:checkCollectionSizeCriteria] Erro:', error); return false;
    }
    return (count ?? 0) >= countRequired;
}

async function checkWishlistSizeCriteria(userId: string, criteria: CriteriaData | null): Promise<boolean> {
    if (!criteria || typeof criteria !== 'object' || !('count_required' in criteria)) return false;
    const countRequired = criteria.count_required as number;
    const { count, error } = await supabase
        .from('user_games')
        .select('game_id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('wishlist', true); 
     if (error) {
        console.error('[BadgeService:checkWishlistSizeCriteria] Erro:', error); return false;
    }
    return (count ?? 0) >= countRequired;
}

async function checkRatingCountCriteria(userId: string, criteria: CriteriaData | null): Promise<boolean> {
    if (!criteria || typeof criteria !== 'object' || !('count_required' in criteria)) return false;
    const countRequired = criteria.count_required as number;
    const { count, error } = await supabase
        .from('user_games')
        .select('game_id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .not('rating', 'is', null); 
     if (error) {
        console.error('[BadgeService:checkRatingCountCriteria] Erro:', error); return false;
    }
    return (count ?? 0) >= countRequired;
}

async function checkComplexityRatingCountCriteria(userId: string, criteria: CriteriaData | null): Promise<boolean> {
    if (!criteria || typeof criteria !== 'object' || !('count_required' in criteria)) return false;
    const countRequired = criteria.count_required as number;
    const { count, error } = await supabase
        .from('user_games')
        .select('game_id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .not('complexity_rating', 'is', null); 
     if (error) {
        console.error('[BadgeService:checkComplexityRatingCountCriteria] Erro:', error); return false;
    }
    return (count ?? 0) >= countRequired;
}


async function checkSessionCountCriteria(userId: string, criteria: CriteriaData | null): Promise<boolean> {
    if (!criteria || typeof criteria !== 'object' || !('count_required' in criteria)) return false;
    const countRequired = criteria.count_required as number;
    const { count, error } = await supabase
        .from('game_sessions')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId); 
     if (error) {
        console.error('[BadgeService:checkSessionCountCriteria] Erro:', error); return false;
    }
    return (count ?? 0) >= countRequired;
}

async function checkConnectionCountCriteria(userId: string, criteria: CriteriaData | null): Promise<boolean> {
    if (!criteria || typeof criteria !== 'object' || !('count_required' in criteria)) return false;
    const countRequired = criteria.count_required as number;
    const { count, error } = await supabase
        .from('user_connections')
        .select('id', { count: 'exact', head: true })
        .or(`user_id.eq.${userId},connected_user_id.eq.${userId}`) 
        .eq('status', 'accepted');
     if (error) {
        console.error('[BadgeService:checkConnectionCountCriteria] Erro:', error); return false;
    }
    return (count ?? 0) >= countRequired;
}

// Funções para especialistas (exemplo para categoria)
async function checkCategorySpecialistCriteria(userId: string, category: string, countRequired: number): Promise<boolean> {
    // Contar jogos na coleção do usuário que pertencem a esta categoria
    const { count, error } = await supabase
        .from('user_games')
        .select('games!inner(id)', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('owned', true)
        .contains('games.categories', [category]); // Assumindo que 'categories' é um array

    if (error) {
        console.error(`[BadgeService:checkCategorySpecialistCriteria] Erro ao contar jogos da categoria ${category}:`, error);
        return false;
    }
    return (count ?? 0) >= countRequired;
}

async function checkThemeSpecialistCriteria(userId: string, theme: string, countRequired: number): Promise<boolean> {
     const { count, error } = await supabase
        .from('user_games')
        .select('games!inner(id)', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('owned', true)
        .contains('games.themes', [theme]); 

    if (error) {
        console.error(`[BadgeService:checkThemeSpecialistCriteria] Erro ao contar jogos do tema ${theme}:`, error);
        return false;
    }
    return (count ?? 0) >= countRequired;
}

async function checkMechanicSpecialistCriteria(userId: string, mechanic: string, countRequired: number): Promise<boolean> {
    const { count, error } = await supabase
        .from('user_games')
        .select('games!inner(id)', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('owned', true)
        .contains('games.mechanics', [mechanic]);

    if (error) {
        console.error(`[BadgeService:checkMechanicSpecialistCriteria] Erro ao contar jogos da mecânica ${mechanic}:`, error);
        return false;
    }
    return (count ?? 0) >= countRequired;
}

// --- NOVAS FUNÇÕES DE CRITÉRIO ---

async function checkBadgesCountCriteria(userId: string, criteria: CriteriaData | null): Promise<boolean> {
    if (!criteria || typeof criteria !== 'object' || !('count_required' in criteria)) return false;
    const countRequired = criteria.count_required as number;

    const { count, error } = await supabase
        .from('user_badges')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId);

    if (error) {
        console.error(`[BadgeService:checkBadgesCountCriteria] Erro ao contar badges:`, error);
        return false;
    }
    return (count ?? 0) >= countRequired;
}

async function checkMarketTransactionsCriteria(userId: string, criteria: CriteriaData | null): Promise<boolean> {
    if (!criteria || typeof criteria !== 'object' || !('count_required' in criteria)) return false;
    const countRequired = criteria.count_required as number;
    const listingType = criteria.listing_type as 'sell' | 'buy' | 'any' | undefined; // Opcional, default 'any'

    let query = supabase
        .from('market_listings')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', 'completed'); // Assumindo que 'completed' significa transação bem-sucedida

    if (listingType && listingType !== 'any') {
        query = query.eq('listing_type', listingType);
    }

    const { count, error } = await query;

    if (error) {
        console.error(`[BadgeService:checkMarketTransactionsCriteria] Erro ao contar transações:`, error);
        return false;
    }
    return (count ?? 0) >= countRequired;
}

// TODO: Implementar funções para outros tipos de critério conforme necessário. 

/**
 * Busca todas as badges definidas no sistema.
 */
export async function getAllDefinedBadges(supabaseInstance: SupabaseClient<Database>): Promise<BadgeRow[]> {
  console.log('[badgeService:getAllDefinedBadges] Fetching all defined badges...');
  const { data, error } = await supabaseInstance
    .from('badges')
    .select('*')
    .order('category', { ascending: true })
    .order('name', { ascending: true });

  if (error) {
    console.error('[badgeService:getAllDefinedBadges] Error fetching badges:', error);
    toast.error(`Erro ao buscar definições de badges: ${error.message}`);
    return [];
  }
  console.log(`[badgeService:getAllDefinedBadges] Fetched ${data?.length || 0} badges.`);
  return data || [];
}

/**
 * Busca todas as badges que um usuário específico desbloqueou, incluindo o status de destaque.
 */
export async function getUserProfileBadges(supabaseInstance: SupabaseClient<Database>, userId: string): Promise<UserProfileBadge[]> {
  if (!userId) {
    console.error('[badgeService:getUserProfileBadges] User ID is required.');
    return [];
  }
  console.log(`[badgeService:getUserProfileBadges] Fetching badges for user ${userId}...`);

  const { data, error } = await supabaseInstance
    .from('user_badges')
    .select(`
      unlocked_at,
      is_highlighted,
      badges (*)
    `)
    .eq('user_id', userId)
    .order('unlocked_at', { ascending: false });

  if (error) {
    console.error(`[badgeService:getUserProfileBadges] Error fetching badges for user ${userId}:`, error);
    toast.error(`Erro ao buscar suas badges: ${error.message}`);
    return [];
  }

  // Mapeia para o formato UserProfileBadge esperado
  const profileBadges = data?.map(ub => ({
    ...(ub.badges as BadgeRow), // Faz o type assertion aqui
    unlocked_at: ub.unlocked_at,
    is_highlighted: ub.is_highlighted
  })) || [];

  console.log(`[badgeService:getUserProfileBadges] Fetched ${profileBadges.length} badges for user ${userId}.`);
  return profileBadges;
}

/**
 * Atualiza quais badges um usuário está destacando em seu perfil.
 * Garante que no máximo 5 badges sejam destacadas.
 */
export async function updateHighlightedBadges(
  supabaseInstance: SupabaseClient<Database>,
  userId: string,
  highlightedBadgeIds: string[]
): Promise<{ success: boolean; error?: string }> {
  if (!userId) {
    return { success: false, error: 'User ID é obrigatório.' };
  }

  if (highlightedBadgeIds.length > 5) {
    return { success: false, error: 'Você pode destacar no máximo 5 badges.' };
  }

  console.log(`[badgeService:updateHighlightedBadges] Updating highlighted badges for user ${userId}:`, highlightedBadgeIds);

  try {
    // Iniciar transação (se o SDK suportar explicitamente ou garantir atomicidade)
    // Passo 1: Remover destaque de todas as badges do usuário
    const { error: unhighlightError } = await supabaseInstance
      .from('user_badges')
      .update({ is_highlighted: false })
      .eq('user_id', userId)
      .eq('is_highlighted', true);

    if (unhighlightError) {
      console.error('[badgeService:updateHighlightedBadges] Error unhighlighting old badges:', unhighlightError);
      throw new Error(`Erro ao remover destaques antigos: ${unhighlightError.message}`);
    }

    // Passo 2: Adicionar destaque às badges selecionadas (se houver alguma)
    if (highlightedBadgeIds.length > 0) {
        const { error: highlightError } = await supabaseInstance
          .from('user_badges')
          .update({ is_highlighted: true })
          .eq('user_id', userId)
          .in('badge_id', highlightedBadgeIds); // Atualiza apenas as badges que o usuário realmente possui e estão na lista

      if (highlightError) {
        console.error('[badgeService:updateHighlightedBadges] Error highlighting new badges:', highlightError);
        // Idealmente, reverter a transação aqui se possível
        throw new Error(`Erro ao aplicar novos destaques: ${highlightError.message}`);
      }
    }

    console.log(`[badgeService:updateHighlightedBadges] Successfully updated highlighted badges for user ${userId}.`);
    toast.success('Badges destacadas atualizadas com sucesso!');
    return { success: true };

  } catch (error: any) {
    console.error('[badgeService:updateHighlightedBadges] Transaction failed:', error);
    toast.error(`Falha ao atualizar badges destacadas: ${error.message}`);
    return { success: false, error: error.message };
  }
}

/**
 * Busca as badges que um usuário selecionou para exibir em seu perfil público.
 * Retorna as badges destacadas (is_highlighted).
 */
export async function getPublicProfileBadges(userId: string): Promise<UserProfileBadge[]> {
  if (!userId) {
    console.warn('[badgeService:getPublicProfileBadges] User ID é obrigatório.');
    return [];
  }

  try {
    // Definir o tipo esperado para a resposta da query explicitamente
    type UserBadgeWithBadge = Pick<UserBadgeRow, 'unlocked_at' | 'is_highlighted'> & { badges: BadgeRow | null };

    const { data, error } = await supabase
      .from('user_badges')
      .select(`
        unlocked_at,
        is_highlighted,
        badges (*)
      `)
      .eq('user_id', userId)
      .eq('is_highlighted', true)
      .returns<UserBadgeWithBadge[]>(); // <<< Adicionar .returns<Type[]>()

    if (error) {
      console.error('[badgeService:getPublicProfileBadges] Error fetching highlighted user badges:', error);
      toast.error('Erro ao buscar badges do perfil.');
      return [];
    }

    if (!data) {
      return [];
    }

    // Mapear para o formato UserProfileBadge, filtrando casos onde 'badges' é null
    const profileBadges: UserProfileBadge[] = data
      .filter(ub => ub.badges !== null) // Garantir que temos os dados da badge
      .map((ub) => ({
        ...(ub.badges as BadgeRow), // Dados da badge (agora seguro após o filter)
        unlocked_at: ub.unlocked_at,
        is_highlighted: ub.is_highlighted,
      }));

    // Ordenar por data de desbloqueio (mais recente primeiro)
    profileBadges.sort((a, b) => {
      if (a.unlocked_at && b.unlocked_at) {
        return new Date(b.unlocked_at).getTime() - new Date(a.unlocked_at).getTime();
      }
      return 0;
    });

    console.log('[badgeService:getPublicProfileBadges] Fetched public badges:', profileBadges);
    return profileBadges;

  } catch (err) {
    console.error('[badgeService:getPublicProfileBadges] Unexpected error:', err);
    toast.error('Um erro inesperado ocorreu ao buscar as badges do perfil.');
    return [];
  }
}

/**
 * Verifica retroativamente uma badge específica para todos os usuários do sistema
 * e a concede se os critérios forem atendidos.
 * @param badgeId O ID da badge a ser verificada retroativamente.
 * @returns Um objeto com o número de usuários processados e o número de badges concedidas.
 */
export async function retroactivelyCheckAndAwardBadge(badgeId: string): Promise<{ processed: number; awarded: number }> {
  console.log(`[BadgeService:retroactivelyCheckAndAwardBadge] Iniciando verificação retroativa para badge ID: ${badgeId}`);
  const badge = await getBadgeById(badgeId);

  if (!badge) {
    console.error(`[BadgeService:retroactivelyCheckAndAwardBadge] Badge com ID ${badgeId} não encontrada.`);
    toast.error(`Badge com ID ${badgeId} não encontrada para verificação retroativa.`);
    return { processed: 0, awarded: 0 };
  }

  if (!badge.is_active) {
    console.warn(`[BadgeService:retroactivelyCheckAndAwardBadge] Badge ${badge.badge_key} (ID: ${badgeId}) não está ativa. Nenhuma ação será tomada.`);
    return { processed: 0, awarded: 0 };
  }

  const allUserIds = await getAllUserIds();
  if (allUserIds.length === 0) {
    console.log('[BadgeService:retroactivelyCheckAndAwardBadge] Nenhum usuário encontrado para processar.');
    return { processed: 0, awarded: 0 };
  }

  let awardedCount = 0;
  console.log(`[BadgeService:retroactivelyCheckAndAwardBadge] Processando ${allUserIds.length} usuários para a badge ${badge.badge_key}.`);

  for (const userId of allUserIds) {
    const wasAwardedNow = await checkAndPotentiallyAwardBadge(userId, badge);
    if (wasAwardedNow) {
      awardedCount++;
      console.log(`[BadgeService:retroactivelyCheckAndAwardBadge] Badge ${badge.badge_key} concedida retroativamente para o usuário ${userId}.`);
    }
  }

  console.log(`[BadgeService:retroactivelyCheckAndAwardBadge] Verificação retroativa para badge ${badge.badge_key} (ID: ${badgeId}) concluída. Usuários processados: ${allUserIds.length}. Badges concedidas: ${awardedCount}.`);
  toast.success(`Verificação retroativa para "${badge.name}" concluída. ${awardedCount} badges concedidas a ${allUserIds.length} usuários.`);
  return { processed: allUserIds.length, awarded: awardedCount };
} 